import React from "react";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import CardCommon from "../../../../components/card/CardCommon";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";

interface ChargebeeCustomer {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
}

interface Props {
  customerId: string | null;
  customer: ChargebeeCustomer | null;
  loading: boolean;
  error: string;
  onConnect: () => void;
  showConnectAllUnlinked?: boolean;
  onConnectAllUnlinked?: () => void;
  onDisconnect?: () => void | Promise<void>;
}

const ChargebeeCustomerCard: React.FC<Props> = ({
  customerId,
  customer,
  loading,
  error,
  onConnect,
  showConnectAllUnlinked = false,
  onConnectAllUnlinked,
  onDisconnect,
}) => {
  return (
    <CardCommon
      backgroundColor="entity_background"
      style={{
        width: "100%",
        marginBottom: 16,
        padding: 16,
        borderRadius: 12,
        boxShadow: "none",
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        <Box width="100%" style={{ marginBottom: 12 }}>
          <Typography
            variant="overline"
            color="textSecondary"
            style={{ letterSpacing: 1.2, display: "block", marginBottom: 4 }}
          >
            Chargebee
          </Typography>
          

          <Box
            width="100%"
            style={{
              padding: 12,
              borderRadius: 8,
              backgroundColor: "rgba(0, 0, 0, 0.03)",
            }}
          >
            {loading ? (
              <Box display="flex" flexDirection="column">
                {[...Array(3)].map((_, index) => (
                  <Box key={index} mb={index === 2 ? 0 : 1}>
                    <Skeleton
                      variant="rect"
                      height={18}
                      style={{ borderRadius: 8 }}
                    />
                  </Box>
                ))}
              </Box>
            ) : error ? (
              <Typography variant="body2" style={{ color: "#d32f2f" }}>
                {error}
              </Typography>
            ) : customer || customerId ? (
              <Box
                width="100%"
                position="relative"
                style={{
                  minHeight:
                    customerId && showConnectAllUnlinked ? 84 : 38,
                }}
              >
                <Box width="100%" textAlign="center">
                  {customer ? (
                    <>
                      <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <strong>Customer ID:</strong> {customer.id}
                      </Typography>
                      <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <strong>First Name:</strong>{" "}
                        {customer.first_name || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Last Name:</strong> {customer.last_name || "-"}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <strong>Customer ID:</strong> {customerId}
                      </Typography>
                      <Typography variant="body2">
                        Customer ID is mapped, but customer details could not be loaded.
                      </Typography>
                    </>
                  )}
                </Box>

                <Box
                  display="flex"
                  flexDirection="column"
                  style={{
                    gap: 8,
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {customerId && onDisconnect && (
                    <ButtonCommon
                      variant="contained"
                      color="red"
                      onClick={onDisconnect}
                      style={{
                        minWidth: 210,
                        height: 38,
                        lineHeight: 1.2,
                        borderRadius: 8,
                        paddingLeft: 16,
                        paddingRight: 16,
                        whiteSpace: "nowrap",
                        textTransform: "none",
                        fontWeight: 500,
                      }}
                    >
                      Disconnect from Chargebee
                    </ButtonCommon>
                  )}

                  {showConnectAllUnlinked && onConnectAllUnlinked && (
                    <ButtonCommon
                      variant="contained"
                      color="green"
                      onClick={onConnectAllUnlinked}
                      style={{
                        minWidth: 210,
                        height: 38,
                        lineHeight: 1.2,
                        borderRadius: 8,
                        paddingLeft: 16,
                        paddingRight: 16,
                        whiteSpace: "nowrap",
                        textTransform: "uppercase",
                        fontWeight: 500,
                      }}
                    >
                      Connect All Unlinked
                    </ButtonCommon>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2">
                No Chargebee customer connected to this location.
              </Typography>
            )}
          </Box>
        </Box>

        {!loading && !customerId && (
          <ButtonCommon
            variant="contained"
            color="green"
            onClick={onConnect}
            style={{
              marginTop: 4,
              minWidth: 200,
              width: "fit-content",
              height: 38,
              lineHeight: 1.2,
              borderRadius: 8,
              paddingLeft: 16,
              paddingRight: 16,
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textTransform: "none",
              fontWeight: 600,
              letterSpacing: 0.2,
              boxShadow: "0 10px 18px rgba(46, 125, 50, 0.18)",
            }}
          >
            Connect to Chargebee
          </ButtonCommon>
        )}
      </Box>
    </CardCommon>
  );
};

export default ChargebeeCustomerCard;
